import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Collapse,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link } from "react-router-dom";
import {
  IconBrain,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconHistory,
  IconMessage,
  IconRobot,
} from "@tabler/icons-react";

import api from "../../services/api";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface QueryLogEntry {
  id: string;
  query: string;
  response: string;
  classification: string | null;
  createdAt: string;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  geminiModel: string | null;
  querySetup: Record<string, unknown> | null;
  claritySessionUrl: string | null;
  threadId: string | null;
  source: string | null;
  menuItemId: string | null;
  thinking: string | null;
  menu?: {
    id: string;
    restaurant?: { id: string; name: string } | null;
  } | null;
  user?: { name: string } | null;
  menuItem?: {
    id: string;
    name: string;
    price: string | null;
    description: string | null;
  } | null;
}

interface Thread {
  threadId: string | null;
  logs: QueryLogEntry[]; // chronological (oldest first)
  restaurantId: string | null;
  restaurantName: string | null;
  menuId: string | null;
  lastActivity: string;
  claritySessionUrl: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const truncate = (str: string, maxLen: number) =>
  str.length <= maxLen ? str : str.slice(0, maxLen) + "…";

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Group logs into threads. Logs without a threadId each become their own pseudo-thread. */
function groupIntoThreads(logs: QueryLogEntry[]): Thread[] {
  const map = new Map<string, QueryLogEntry[]>();
  const order: string[] = [];

  for (const log of logs) {
    const key = log.threadId ?? `solo-${log.id}`;
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(log);
  }

  return order.map((key) => {
    const items = map.get(key)!;
    const sorted = [...items].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    const newest = items[0]; // API returns newest-first
    return {
      threadId: sorted[0].threadId,
      logs: sorted,
      restaurantId: newest.menu?.restaurant?.id ?? null,
      restaurantName: newest.menu?.restaurant?.name ?? null,
      menuId: newest.menu?.id ?? null,
      lastActivity: newest.createdAt,
      claritySessionUrl: newest.claritySessionUrl,
    };
  });
}

/** Build the JSON payload to copy for a thread. */
function buildThreadJson(thread: Thread): object {
  const firstLog = thread.logs[0];
  const isMenuItemSolo = thread.logs.length === 1 && firstLog.source === "menu_item";

  if (isMenuItemSolo) {
    return {
      type: "menu_item_faq",
      menuItemId: firstLog.menuItemId,
      menuId: thread.menuId,
      restaurant: thread.restaurantName,
      timestamp: firstLog.createdAt,
      question: firstLog.query,
      answer: firstLog.response,
      model: firstLog.geminiModel,
      claritySessionUrl: thread.claritySessionUrl,
    };
  }

  return {
    type: "menu_chat_thread",
    threadId: thread.threadId,
    restaurant: thread.restaurantName,
    menuId: thread.menuId,
    lastActivity: thread.lastActivity,
    claritySessionUrl: thread.claritySessionUrl,
    turns: thread.logs.map((log) => ({
      timestamp: log.createdAt,
      query: log.query,
      response: log.response,
      ...(log.thinking ? { thinking: log.thinking } : {}),
      classification: log.classification,
      model: log.geminiModel,
      tokens: log.totalTokens,
    })),
  };
}

// ---------------------------------------------------------------------------
// CopyButton — copies JSON, shows brief ✓ feedback
// ---------------------------------------------------------------------------

function CopyButton({ data }: { data: object }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // fallback: create a temporary textarea
      const ta = document.createElement("textarea");
      ta.value = JSON.stringify(data, null, 2);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  }, [data]);

  return (
    <Tooltip label={copied ? "Copied!" : "Copy as JSON"} withArrow>
      <Button
        size="xs"
        variant="light"
        color={copied ? "green" : "gray"}
        leftSection={copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
        onClick={handleCopy}
      >
        {copied ? "Copied!" : "Copy JSON"}
      </Button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// ThinkingBlock — collapsible reasoning panel
// ---------------------------------------------------------------------------

function ThinkingBlock({ thinking }: { thinking: string }) {
  const [open, { toggle }] = useDisclosure(false);
  return (
    <Box mt={6}>
      <UnstyledButton onClick={toggle}>
        <Group gap={4} align="center">
          <IconBrain size={13} color="var(--mantine-color-violet-6)" />
          <Text size="xs" c="violet" fw={500}>
            {open ? "Hide thinking" : "Show thinking"}
          </Text>
          {open ? (
            <IconChevronUp size={12} color="var(--mantine-color-violet-6)" />
          ) : (
            <IconChevronDown size={12} color="var(--mantine-color-violet-6)" />
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>
        <Paper
          mt={4}
          p="sm"
          radius="sm"
          style={{
            background: "var(--mantine-color-violet-0)",
            borderLeft: "3px solid var(--mantine-color-violet-4)",
          }}
        >
          <ScrollArea.Autosize mah={220}>
            <Text size="xs" c="dimmed" style={{ whiteSpace: "pre-wrap", fontStyle: "italic" }}>
              {thinking}
            </Text>
          </ScrollArea.Autosize>
        </Paper>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TurnBubble — one user→AI exchange
// ---------------------------------------------------------------------------

function TurnBubble({ log }: { log: QueryLogEntry }) {
  return (
    <Stack gap={6}>
      {/* User message */}
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <Box
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "var(--mantine-color-blue-6)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconMessage size={13} color="white" />
        </Box>
        <Box style={{ flex: 1 }}>
          <Paper
            p="sm"
            radius="md"
            style={{
              background: "var(--mantine-color-blue-0)",
              border: "1px solid var(--mantine-color-blue-2)",
            }}
          >
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {log.query}
            </Text>
            {log.menuItem && (
              <Box
                mt={6}
                p="xs"
                style={{
                  background: "var(--mantine-color-teal-0)",
                  border: "1px solid var(--mantine-color-teal-2)",
                  borderRadius: 6,
                }}
              >
                <Text size="xs" fw={600} c="teal.8">
                  {log.menuItem.name}
                  {log.menuItem.price != null && (
                    <Text span c="dimmed" fw={400}> · ¥{log.menuItem.price}</Text>
                  )}
                </Text>
                {log.menuItem.description && (
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {log.menuItem.description}
                  </Text>
                )}
              </Box>
            )}
          </Paper>
          <Text size="xs" c="dimmed" mt={2} ml={2}>
            {formatTime(log.createdAt)}
          </Text>
        </Box>
      </Group>

      {/* AI response */}
      <Group align="flex-start" gap="xs" wrap="nowrap">
        <Box
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            background: "var(--mantine-color-orange-5)",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconRobot size={13} color="white" />
        </Box>
        <Box style={{ flex: 1 }}>
          <Paper
            p="sm"
            radius="md"
            style={{
              background: "var(--mantine-color-gray-0)",
              border: "1px solid var(--mantine-color-gray-2)",
            }}
          >
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {log.response}
            </Text>
            {log.thinking && <ThinkingBlock thinking={log.thinking} />}
          </Paper>
          <Group gap="xs" mt={2} ml={2} wrap="wrap">
            {log.classification && (
              <Badge size="xs" variant="light">
                {log.classification}
              </Badge>
            )}
            {log.source && (
              <Badge
                size="xs"
                variant="filled"
                color={log.source === "menu_item" ? "teal" : "blue"}
              >
                {log.source}
              </Badge>
            )}
            {log.geminiModel && (
              <Badge size="xs" variant="outline" color="gray">
                {log.geminiModel}
              </Badge>
            )}
            {log.totalTokens != null && (
              <Text size="xs" c="dimmed">
                {log.totalTokens.toLocaleString()} tokens
              </Text>
            )}
            {log.menuItemId && (
              <Text size="xs" c="dimmed" ff="monospace">
                item: {truncate(log.menuItemId, 8)}…
              </Text>
            )}
          </Group>
        </Box>
      </Group>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// ThreadCard — one collapsible thread (or solo item)
// ---------------------------------------------------------------------------

function ThreadCard({ thread }: { thread: Thread }) {
  const isThread = thread.logs.length > 1;
  const [open, { toggle }] = useDisclosure(false);

  const firstLog = thread.logs[0];
  const lastLog = thread.logs[thread.logs.length - 1];
  const previewQuery = truncate(firstLog.query, 80);
  const copyData = buildThreadJson(thread);

  return (
    <Paper withBorder radius="md" p={0} style={{ overflow: "hidden" }}>
      {/* Header — always visible */}
      <UnstyledButton
        onClick={toggle}
        style={{ width: "100%", padding: "12px 16px", display: "block" }}
      >
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500} lineClamp={1}>
              {previewQuery}
            </Text>
            <Group gap="xs" wrap="wrap">
              {thread.restaurantName && (
                <Text size="xs" c="dimmed">
                  {thread.restaurantName}
                </Text>
              )}
              <Text size="xs" c="dimmed">
                {formatTime(thread.lastActivity)}
              </Text>
              {isThread && (
                <Badge size="xs" variant="light" color="gray">
                  {thread.logs.length} turns
                </Badge>
              )}
              {lastLog.source && (
                <Badge
                  size="xs"
                  variant="filled"
                  color={lastLog.source === "menu_item" ? "teal" : "blue"}
                >
                  {lastLog.source}
                </Badge>
              )}
              {thread.logs.some((l) => l.thinking) && (
                <Badge
                  size="xs"
                  variant="light"
                  color="violet"
                  leftSection={<IconBrain size={10} />}
                >
                  thinking
                </Badge>
              )}
              {thread.claritySessionUrl && (
                <Text
                  size="xs"
                  c="blue"
                  component="a"
                  href={thread.claritySessionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Clarity ↗
                </Text>
              )}
            </Group>
          </Stack>
          <Box style={{ flexShrink: 0, color: "var(--mantine-color-dimmed)" }}>
            {open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Box>
        </Group>
      </UnstyledButton>

      {/* Expanded conversation */}
      <Collapse in={open}>
        <Box p="md" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
          <Stack gap="xl">
            {thread.logs.map((log) => (
              <TurnBubble key={log.id} log={log} />
            ))}
          </Stack>

          {/* Thread footer */}
          <Box mt="md" pt="sm" style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}>
            <Group justify="space-between" wrap="wrap" gap="sm">
              {/* Left: meta links */}
              <Group gap="md" wrap="wrap">
                {thread.threadId && (
                  <Text size="xs" c="dimmed" ff="monospace">
                    thread: {thread.threadId}
                  </Text>
                )}
                {firstLog.menu && (
                  <Text size="xs" c="dimmed">
                    <Link to={`/admin/menus/edit/${firstLog.menu.id}`}>Edit menu →</Link>
                  </Text>
                )}
                {thread.claritySessionUrl && (
                  <Text size="xs">
                    <a
                      href={thread.claritySessionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Clarity session →
                    </a>
                  </Text>
                )}
              </Group>

              {/* Right: copy button */}
              <CopyButton data={copyData} />
            </Group>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// QueryHistory page
// ---------------------------------------------------------------------------

export function QueryHistory() {
  const [logs, setLogs] = useState<QueryLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restaurantFilter, setRestaurantFilter] = useState<string | null>(null);
  const [menuFilter, setMenuFilter] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.get<QueryLogEntry[]>("/query-logs");
        setLogs(response.data ?? []);
      } catch (err: unknown) {
        const message =
          err && typeof err === "object" && "response" in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : "Failed to load query history";
        setError(String(message));
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const allThreads = useMemo(() => groupIntoThreads(logs), [logs]);

  // Derive unique restaurants from threads (stable, sorted by name)
  const restaurantOptions = useMemo(() => {
    const seen = new Map<string, string>(); // id → name
    for (const t of allThreads) {
      if (t.restaurantId && t.restaurantName) {
        seen.set(t.restaurantId, t.restaurantName);
      }
    }
    return Array.from(seen.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ value: id, label: name }));
  }, [allThreads]);

  // Derive unique menus for the selected restaurant (or all menus if no restaurant filter)
  const menuOptions = useMemo(() => {
    const seen = new Map<string, string>(); // menuId → display label
    for (const t of allThreads) {
      if (!restaurantFilter || t.restaurantId === restaurantFilter) {
        if (t.menuId) {
          const label = t.restaurantName
            ? `${t.restaurantName} — ${t.menuId.slice(0, 8)}…`
            : t.menuId.slice(0, 8) + "…";
          seen.set(t.menuId, label);
        }
      }
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ value: id, label }));
  }, [allThreads, restaurantFilter]);

  // Apply filters
  const threads = useMemo(() => {
    return allThreads.filter((t) => {
      if (restaurantFilter && t.restaurantId !== restaurantFilter) return false;
      if (menuFilter && t.menuId !== menuFilter) return false;
      return true;
    });
  }, [allThreads, restaurantFilter, menuFilter]);

  const handleRestaurantChange = (val: string | null) => {
    setRestaurantFilter(val);
    setMenuFilter(null); // reset menu filter when restaurant changes
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        <Title order={2}>Query history</Title>
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={2}>Query history</Title>
        <Text c="red">{error}</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group>
        <IconHistory size={28} />
        <Title order={2}>Query history</Title>
      </Group>
      <Text size="sm" c="dimmed">
        Recent queries grouped by conversation thread (last 100). Click a thread to expand the full
        chat. Threads with thinking show a{" "}
        <Badge size="xs" variant="light" color="violet" leftSection={<IconBrain size={10} />}>
          thinking
        </Badge>{" "}
        badge. Use the copy button in a thread footer to export as JSON.
      </Text>

      {/* Filters */}
      <Group gap="sm" wrap="wrap">
        <Select
          placeholder="All restaurants"
          data={restaurantOptions}
          value={restaurantFilter}
          onChange={handleRestaurantChange}
          clearable
          searchable
          style={{ minWidth: 220 }}
          size="sm"
        />
        <Select
          placeholder="All menus"
          data={menuOptions}
          value={menuFilter}
          onChange={setMenuFilter}
          clearable
          searchable
          disabled={menuOptions.length === 0}
          style={{ minWidth: 260 }}
          size="sm"
        />
        {(restaurantFilter || menuFilter) && (
          <Text size="xs" c="dimmed" style={{ alignSelf: "center" }}>
            {threads.length} / {allThreads.length} threads
          </Text>
        )}
      </Group>

      {threads.length === 0 ? (
        <Text c="dimmed">
          {allThreads.length === 0 ? "No query logs yet." : "No threads match the current filters."}
        </Text>
      ) : (
        <Stack gap="sm">
          {threads.map((thread) => (
            <ThreadCard key={thread.threadId ?? thread.logs[0].id} thread={thread} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
